import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, Phone, Share2, Mail, Eye, EyeOff, Save, CheckCircle, LogOut, ChevronRight, Instagram, MessageCircle, Send, Facebook, Youtube, ExternalLink, AlertTriangle, Package, MapPin, BarChart2, ChevronDown, ChevronUp, Plus, Trash2, Image, Search, Layout, Handshake } from 'lucide-react';
import { getSiteConfig, saveSiteConfig, getAdminPassword, setAdminPassword } from '../utils/siteConfig';
import { getEmailConfig, saveEmailConfig, EmailConfig } from '../utils/emailService';
import { getContent, saveContent, getDefaultContent, Product, PreviewProduct, StatItem, AreaBlock, PartnerBenefit, PartnerStep, TeamMember } from '../utils/contentStore';
import { ICON_NAMES, renderIcon } from '../utils/iconMap';
import { getSEO, saveSEO, SEOStore, PageSEO } from '../utils/seoStore';
import { Users as UsersIcon } from 'lucide-react';
import { Language } from '../utils/translations';
type Section = 'contacts' | 'social' | 'email' | 'catalog' | 'preview' | 'stats' | 'map' | 'seo' | 'areas' | 'partner' | 'team' | 'password' | 'hero' | 'about' | 'clients';
const NAV: {
  id: Section;
  label: string;
  icon: React.ReactNode;
  group?: string;
}[] = [{
  id: 'hero',
  label: 'Главная — Hero',
  icon: <Layout className="h-4 w-4" data-id="element-422" />,
  group: 'Контент'
}, {
  id: 'about',
  label: 'Главная — О компании',
  icon: <Layout className="h-4 w-4" data-id="element-423" />,
  group: 'Контент'
}, {
  id: 'preview',
  label: 'Главная — Каталог',
  icon: <Image className="h-4 w-4" data-id="element-424" />,
  group: 'Контент'
}, {
  id: 'areas',
  label: 'Главная — Сферы',
  icon: <Layout className="h-4 w-4" data-id="element-425" />,
  group: 'Контент'
}, {
  id: 'team',
  label: 'Наша команда',
  icon: <UsersIcon className="h-4 w-4" data-id="element-426" />,
  group: 'Контент'
}, {
  id: 'clients',
  label: 'Клиенты',
  icon: <UsersIcon className="h-4 w-4" data-id="element-427" />,
  group: 'Контент'
}, {
  id: 'catalog',
  label: 'Страница каталога',
  icon: <Package className="h-4 w-4" data-id="element-428" />,
  group: 'Контент'
}, {
  id: 'partner',
  label: 'Страница партнеров',
  icon: <Handshake className="h-4 w-4" data-id="element-429" />,
  group: 'Контент'
}, {
  id: 'stats',
  label: 'Статистика',
  icon: <BarChart2 className="h-4 w-4" data-id="element-430" />,
  group: 'Контент'
}, {
  id: 'map',
  label: 'Карта',
  icon: <MapPin className="h-4 w-4" data-id="element-431" />,
  group: 'Контент'
}, {
  id: 'seo',
  label: 'SEO / Мета-теги',
  icon: <Search className="h-4 w-4" data-id="element-432" />,
  group: 'Контент'
}, {
  id: 'contacts',
  label: 'Контакты',
  icon: <Phone className="h-4 w-4" data-id="element-433" />,
  group: 'Настройки'
}, {
  id: 'social',
  label: 'Соц. сети',
  icon: <Share2 className="h-4 w-4" data-id="element-434" />,
  group: 'Настройки'
}, {
  id: 'email',
  label: 'Формы / Email',
  icon: <Mail className="h-4 w-4" data-id="element-435" />,
  group: 'Настройки'
}, {
  id: 'password',
  label: 'Безопасность',
  icon: <Lock className="h-4 w-4" data-id="element-436" />,
  group: 'Настройки'
}];
function SaveBanner({
  show
}: {
  show: boolean;
}) {
  return <AnimatePresence data-id="element-437">
      {show && <motion.div initial={{
      opacity: 0,
      y: -20
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: -20
    }} className="fixed top-6 right-6 z-50 bg-green-600 text-white px-5 py-3 rounded-sm shadow-xl flex items-center gap-2 text-sm font-medium" data-id="element-438">
          <CheckCircle className="h-4 w-4" data-id="element-439" /> Сохранено
        </motion.div>}
    </AnimatePresence>;
}
function Field({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  hint
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  hint?: string;
}) {
  return <div className="space-y-1.5" data-id="element-440">
      <label className="text-sm font-medium text-gray-300" data-id="element-441">{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-sm text-white placeholder-gray-500 focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none transition-all text-sm" data-id="element-442" />

      {hint && <p className="text-xs text-gray-500" data-id="element-443">{hint}</p>}
    </div>;
}
function TextArea({
  label,
  value,
  onChange,
  placeholder,
  rows = 3
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
}) {
  return <div className="space-y-1.5" data-id="element-444">
      <label className="text-sm font-medium text-gray-300" data-id="element-445">{label}</label>
      <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={rows} className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-sm text-white placeholder-gray-500 focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none transition-all text-sm resize-none" data-id="element-446" />
    </div>;
}
function SaveBtn({
  onClick,
  label = 'Сохранить'
}: {
  onClick: () => void;
  label?: string;
}) {
  return <button onClick={onClick} className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-medium uppercase tracking-wide px-6 py-3 rounded-sm transition-colors text-sm" data-id="element-447">
      <Save className="h-4 w-4" data-id="element-448" /> {label}
    </button>;
}
// ── Icon Picker ───────────────────────────────────────────────────
function IconPicker({
  value,
  onChange
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  return <div className="space-y-1.5" data-id="element-449">
      <label className="text-sm font-medium text-gray-300" data-id="element-450">Иконка</label>
      <button type="button" onClick={() => setOpen(!open)} className="flex items-center gap-3 px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-sm text-white text-sm w-full hover:border-gray-500 transition-colors" data-id="element-451">
        <span className="text-gray-300" data-id="element-452">{renderIcon(value, 'h-5 w-5')}</span>
        <span data-id="element-453">{value}</span>
        <ChevronDown className="h-4 w-4 text-gray-500 ml-auto" data-id="element-454" />
      </button>
      {open && <div className="bg-gray-800 border border-gray-700 rounded-sm p-3 grid grid-cols-6 gap-2 max-h-48 overflow-y-auto" data-id="element-455">
          {ICON_NAMES.map(name => <button key={name} type="button" title={name} onClick={() => {
        onChange(name);
        setOpen(false);
      }} className={`p-2 rounded-sm flex items-center justify-center transition-colors ${value === name ? 'bg-red-600 text-white' : 'text-gray-400 hover:bg-gray-700 hover:text-white'}`} data-id="element-456">
              {renderIcon(name, 'h-5 w-5')}
            </button>)}
        </div>}
    </div>;
}
// ── Preview Products (Homepage) ───────────────────────────────────
function PreviewSection({
  onSave,
  adminLang
}: {
  onSave: () => void;
  adminLang: Language;
}) {
  const [products, setProducts] = useState<PreviewProduct[]>(getContent().previewProducts);
  const [open, setOpen] = useState<number | null>(0);
  const update = (idx: number, field: keyof PreviewProduct, val: string) => {
    setProducts(prev => prev.map((p, i) => {
      if (i === idx) {
        if (field === 'image') {
          return {
            ...p,
            [field]: val
          };
        }
        return {
          ...p,
          [field]: {
            ...p[field as keyof PreviewProduct],
            [adminLang]: val
          }
        };
      }
      return p;
    }));
  };
  const save = () => {
    const c = getContent();
    saveContent({
      ...c,
      previewProducts: products
    });
    onSave();
  };
  return <div className="space-y-6" data-id="element-457">
      <div data-id="element-458">
        <h2 className="text-xl font-heading font-bold text-white mb-1" data-id="element-459">
          Главная — Популярные решения
        </h2>
        <p className="text-gray-400 text-sm" data-id="element-460">
          4 карточки товаров на главной странице.
        </p>
      </div>
      <div className="space-y-3" data-id="element-461">
        {products.map((p, idx) => <div key={p.id} className="bg-gray-900 border border-gray-700 rounded-sm overflow-hidden" data-id="element-462">
            <button onClick={() => setOpen(open === idx ? null : idx)} className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-800 transition-colors" data-id="element-463">
              <div className="flex items-center gap-3" data-id="element-464">
                <img src={p.image} alt={p.name.ru} className="w-10 h-10 object-cover rounded-sm bg-gray-700" onError={e => {
              ;
              (e.target as HTMLImageElement).style.display = 'none';
            }} data-id="element-465" />

                <div data-id="element-466">
                  <p className="text-white font-medium text-sm" data-id="element-467">
                    {p.name[adminLang] || p.name.ru}
                  </p>
                  <p className="text-gray-500 text-xs" data-id="element-468">
                    {p.category[adminLang] || p.category.ru}
                  </p>
                </div>
              </div>
              {open === idx ? <ChevronUp className="h-4 w-4 text-gray-400" data-id="element-469" /> : <ChevronDown className="h-4 w-4 text-gray-400" data-id="element-470" />}
            </button>
            {open === idx && <div className="px-5 pb-5 space-y-4 border-t border-gray-700 pt-4" data-id="element-471">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-472">
                  <Field label="Название" value={p.name[adminLang] || ''} onChange={v => update(idx, 'name', v)} data-id="element-473" />

                  <Field label="Категория" value={p.category[adminLang] || ''} onChange={v => update(idx, 'category', v)} data-id="element-474" />
                </div>
                <Field label="URL картинки" value={p.image} onChange={v => update(idx, 'image', v)} placeholder="https://images.unsplash.com/..." data-id="element-475" />

                {p.image && <img src={p.image} alt="preview" className="h-32 w-full object-cover rounded-sm bg-gray-700" data-id="element-476" />}
                <TextArea label="Описание" value={p.description[adminLang] || ''} onChange={v => update(idx, 'description', v)} rows={2} data-id="element-477" />
              </div>}
          </div>)}
      </div>
      <SaveBtn onClick={save} label="Сохранить превью" data-id="element-478" />
    </div>;
}
// ── Full Catalog ──────────────────────────────────────────────────
function CatalogSection({
  onSave,
  adminLang
}: {
  onSave: () => void;
  adminLang: Language;
}) {
  const [products, setProducts] = useState<Product[]>(getContent().products);
  const [showPrices, setShowPrices] = useState(getSiteConfig().catalog.showPrices);
  const [open, setOpen] = useState<number | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const update = (idx: number, field: keyof Product, val: any) => {
    setProducts(prev => prev.map((p, i) => {
      if (i !== idx) return p;
      if (field === 'image' || field === 'price' || field === 'id') {
        return {
          ...p,
          [field]: val
        };
      }
      return {
        ...p,
        [field]: {
          ...(p[field] as any),
          [adminLang]: val
        }
      };
    }));
  };
  const updateSpec = (pIdx: number, sIdx: number, field: 'label' | 'value', val: string) => {
    setProducts(prev => prev.map((p, i) => {
      if (i !== pIdx) return p;
      const specs = p.specs.map((s, j) => j === sIdx ? {
        ...s,
        [field]: {
          ...s[field],
          [adminLang]: val
        }
      } : s);
      return {
        ...p,
        specs
      };
    }));
  };
  const addSpec = (pIdx: number) => {
    setProducts(prev => prev.map((p, i) => i === pIdx ? {
      ...p,
      specs: [...p.specs, {
        label: {
          ru: '',
          ky: ''
        },
        value: {
          ru: '',
          ky: ''
        }
      }]
    } : p));
  };
  const removeSpec = (pIdx: number, sIdx: number) => {
    setProducts(prev => prev.map((p, i) => i === pIdx ? {
      ...p,
      specs: p.specs.filter((_, j) => j !== sIdx)
    } : p));
  };
  const updateAdvantage = (pIdx: number, aIdx: number, val: string) => {
    setProducts(prev => prev.map((p, i) => {
      if (i !== pIdx) return p;
      const advantages = p.advantages.map((a, j) => j === aIdx ? {
        ...a,
        [adminLang]: val
      } : a);
      return {
        ...p,
        advantages
      };
    }));
  };
  const addAdvantage = (pIdx: number) => {
    setProducts(prev => prev.map((p, i) => i === pIdx ? {
      ...p,
      advantages: [...(p.advantages || []), {
        ru: '',
        ky: ''
      }]
    } : p));
  };
  const removeAdvantage = (pIdx: number, aIdx: number) => {
    setProducts(prev => prev.map((p, i) => i === pIdx ? {
      ...p,
      advantages: p.advantages.filter((_, j) => j !== aIdx)
    } : p));
  };
  const toggleActive = (idx: number) => {
    setProducts(prev => prev.map((p, i) => i === idx ? {
      ...p,
      active: !p.active
    } : p));
  };
  const updateParentLine = (idx: number, val: string) => {
    setProducts(prev => prev.map((p, i) => i === idx ? {
      ...p,
      parentLine: val || undefined
    } : p));
  };
  // Get all possible parent lines (products that have no parentLine themselves)
  const possibleLines = products.filter(p => !p.parentLine).map(p => ({
    id: p.id,
    name: p.name
  }));
  const addProduct = () => {
    const newProduct: Product = {
      id: `product_${Date.now()}`,
      name: {
        ru: 'НОВЫЙ ТОВАР',
        ky: 'ЖАҢЫ ТОВАР'
      },
      subtitle: {
        ru: 'Подзаголовок',
        ky: 'Кошумча тема'
      },
      category: {
        ru: 'Категория',
        ky: 'Категория'
      },
      price: 0,
      unit: {
        ru: '20кг',
        ky: '20кг'
      },
      image: '',
      description: {
        ru: '',
        ky: ''
      },
      advantages: [],
      specs: [],
      active: true
    };
    setProducts(prev => [...prev, newProduct]);
    setOpen(products.length);
  };
  const deleteProduct = (idx: number) => {
    setProducts(prev => prev.filter((_, i) => i !== idx));
    setDeleteConfirm(null);
    setOpen(null);
  };
  const save = () => {
    const c = getContent();
    saveContent({
      ...c,
      products
    });
    const cfg = getSiteConfig();
    saveSiteConfig({
      ...cfg,
      catalog: {
        ...cfg.catalog,
        showPrices
      }
    });
    onSave();
  };
  return <div className="space-y-6" data-id="element-479">
      <div className="flex items-start justify-between" data-id="element-480">
        <div data-id="element-481">
          <h2 className="text-xl font-heading font-bold text-white mb-1" data-id="element-482">
            Страница каталога
          </h2>
          <p className="text-gray-400 text-sm" data-id="element-483">
            Все товары с описаниями, ценами и характеристиками.
          </p>
        </div>
        <button onClick={addProduct} className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 border border-gray-600 text-white text-sm px-4 py-2 rounded-sm transition-colors" data-id="element-484">
          <Plus className="h-4 w-4" data-id="element-485" /> Добавить товар
        </button>
      </div>

      {/* Show Prices Toggle */}
      <div className="bg-gray-900 border border-gray-700 rounded-sm p-4 flex items-center justify-between" data-id="element-486">
        <div data-id="element-487">
          <p className="text-white font-medium text-sm" data-id="element-488">
            Показывать цены в каталоге
          </p>
          <p className="text-gray-500 text-xs mt-0.5" data-id="element-489">
            Если отключено, цены не будут видны покупателям
          </p>
        </div>
        <label className="flex items-center gap-3 cursor-pointer" data-id="element-490">
          <div onClick={() => setShowPrices(!showPrices)} className={`w-11 h-6 rounded-full relative transition-colors cursor-pointer ${showPrices ? 'bg-green-600' : 'bg-gray-600'}`} data-id="element-491">
            <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${showPrices ? 'translate-x-5' : 'translate-x-0.5'}`} data-id="element-492" />
          </div>
          <span className={`text-sm font-medium ${showPrices ? 'text-green-400' : 'text-gray-500'}`} data-id="element-493">
            {showPrices ? 'Включено' : 'Отключено'}
          </span>
        </label>
      </div>

      <div className="space-y-3" data-id="element-494">
        {products.map((p, idx) => <div key={p.id} className="bg-gray-900 border border-gray-700 rounded-sm overflow-hidden" data-id="element-495">
            <button onClick={() => setOpen(open === idx ? null : idx)} className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-800 transition-colors" data-id="element-496">
              <div className="flex items-center gap-3 min-w-0" data-id="element-497">
                {p.image ? <img src={p.image} alt={p.name[adminLang] || p.name.ru} className="w-10 h-10 object-cover object-center rounded-sm bg-gray-700 flex-shrink-0" onError={e => {
              ;
              (e.target as HTMLImageElement).style.display = 'none';
            }} data-id="element-498" /> : <div className="w-10 h-10 bg-gray-700 rounded-sm flex-shrink-0 flex items-center justify-center" data-id="element-499">
                    <Package className="h-4 w-4 text-gray-500" data-id="element-500" />
                  </div>}
                <div className="min-w-0" data-id="element-501">
                  <p className={`font-medium text-sm truncate ${p.active === false ? 'text-gray-500 line-through' : 'text-white'}`} data-id="element-502">
                    {p.name[adminLang] || p.name.ru}
                  </p>
                  <p className="text-gray-500 text-xs truncate" data-id="element-503">
                    {p.parentLine ? `↳ линейка: ${possibleLines.find(l => l.id === p.parentLine)?.name[adminLang] || p.parentLine}` : p.subtitle[adminLang] || p.subtitle.ru}
                  </p>
                </div>
                {p.active === false && <span className="ml-2 text-xs bg-gray-700 text-gray-400 px-2 py-0.5 rounded flex-shrink-0" data-id="element-504">
                    Скрыт
                  </span>}
                <span className="ml-2 text-gray-400 text-sm font-medium flex-shrink-0" data-id="element-505">
                  {p.price > 0 ? `${p.price} с` : '—'}
                </span>
              </div>
              {open === idx ? <ChevronUp className="h-4 w-4 text-gray-400 flex-shrink-0" data-id="element-506" /> : <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" data-id="element-507" />}
            </button>

            {open === idx && <div className="px-5 pb-5 space-y-4 border-t border-gray-700 pt-4" data-id="element-508">
                {/* Active toggle & Parent Line */}
                <div className="flex flex-wrap items-center gap-6 pb-4 border-b border-gray-700" data-id="element-509">
                  <label className="flex items-center gap-3 cursor-pointer" data-id="element-510">
                    <div onClick={() => toggleActive(idx)} className={`w-11 h-6 rounded-full relative transition-colors cursor-pointer ${p.active !== false ? 'bg-green-600' : 'bg-gray-600'}`} data-id="element-511">
                      <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${p.active !== false ? 'translate-x-5' : 'translate-x-0.5'}`} data-id="element-512" />
                    </div>
                    <span className={`text-sm font-medium ${p.active !== false ? 'text-green-400' : 'text-gray-500'}`} data-id="element-513">
                      {p.active !== false ? 'Активен' : 'Скрыт'}
                    </span>
                  </label>

                  <div className="flex items-center gap-2" data-id="element-514">
                    <span className="text-gray-400 text-sm" data-id="element-515">Линейка:</span>
                    <select value={p.parentLine || ''} onChange={e => updateParentLine(idx, e.target.value)} className="bg-gray-800 border border-gray-600 text-white text-sm rounded-sm px-3 py-1.5 focus:ring-1 focus:ring-red-500 outline-none" data-id="element-516">
                      <option value="" data-id="element-517">— Нет (самостоятельный)</option>
                      {possibleLines.filter(l => l.id !== p.id).map(l => <option key={l.id} value={l.id} data-id="element-518">
                            {l.name[adminLang] || l.name.ru}
                          </option>)}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-519">
                  <Field label="Название" value={p.name[adminLang] || ''} onChange={v => update(idx, 'name', v)} data-id="element-520" />

                  <Field label="Подзаголовок" value={p.subtitle[adminLang] || ''} onChange={v => update(idx, 'subtitle', v)} data-id="element-521" />

                  <Field label="Категория" value={p.category?.[adminLang] || ''} onChange={v => update(idx, 'category', v)} data-id="element-522" />

                  <Field label="Единица (напр. 20кг)" value={p.unit[adminLang] || ''} onChange={v => update(idx, 'unit', v)} data-id="element-523" />

                  <Field label="Цена (сом)" value={String(p.price)} onChange={v => update(idx, 'price', Number(v) || 0)} type="number" data-id="element-524" />

                  <Field label="ID товара (для URL)" value={p.id} onChange={v => update(idx, 'id', v)} data-id="element-525" />
                </div>

                <Field label="URL картинки" value={p.image} onChange={v => update(idx, 'image', v)} placeholder="https://images.unsplash.com/..." data-id="element-526" />

                {p.image && <img src={p.image} alt="preview" className="h-40 w-full object-contain object-center rounded-sm bg-gray-700" data-id="element-527" />}

                <TextArea label="Описание" value={p.description[adminLang] || ''} onChange={v => update(idx, 'description', v)} rows={3} data-id="element-528" />

                {/* Advantages */}
                <div data-id="element-529">
                  <div className="flex items-center justify-between mb-3" data-id="element-530">
                    <label className="text-sm font-medium text-gray-300" data-id="element-531">
                      Преимущества
                    </label>
                    <button onClick={() => addAdvantage(idx)} className="flex items-center gap-1 text-xs text-red-400 hover:text-red-300" data-id="element-532">
                      <Plus className="h-3 w-3" data-id="element-533" /> Добавить
                    </button>
                  </div>
                  <div className="space-y-2" data-id="element-534">
                    {(p.advantages || []).map((adv, aIdx) => <div key={aIdx} className="flex gap-2 items-center" data-id="element-535">
                        <input value={adv[adminLang] || ''} onChange={e => updateAdvantage(idx, aIdx, e.target.value)} placeholder="Преимущество" className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-sm text-white text-xs outline-none focus:ring-1 focus:ring-red-500" data-id="element-536" />

                        <button onClick={() => removeAdvantage(idx, aIdx)} className="text-gray-600 hover:text-red-400 transition-colors" data-id="element-537">
                          <Trash2 className="h-4 w-4" data-id="element-538" />
                        </button>
                      </div>)}
                  </div>
                </div>

                {/* Specs */}
                <div data-id="element-539">
                  <div className="flex items-center justify-between mb-3" data-id="element-540">
                    <label className="text-sm font-medium text-gray-300" data-id="element-541">
                      Технические характеристики
                    </label>
                    <button onClick={() => addSpec(idx)} className="flex items-center gap-1 text-xs text-red-400 hover:text-red-300" data-id="element-542">
                      <Plus className="h-3 w-3" data-id="element-543" /> Добавить
                    </button>
                  </div>
                  <div className="space-y-2" data-id="element-544">
                    {p.specs.map((spec, sIdx) => <div key={sIdx} className="flex gap-2 items-center" data-id="element-545">
                        <input value={spec.label[adminLang] || ''} onChange={e => updateSpec(idx, sIdx, 'label', e.target.value)} placeholder="Параметр" className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-sm text-white text-xs outline-none focus:ring-1 focus:ring-red-500" data-id="element-546" />

                        <input value={spec.value[adminLang] || ''} onChange={e => updateSpec(idx, sIdx, 'value', e.target.value)} placeholder="Значение" className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-sm text-white text-xs outline-none focus:ring-1 focus:ring-red-500" data-id="element-547" />

                        <button onClick={() => removeSpec(idx, sIdx)} className="text-gray-600 hover:text-red-400 transition-colors" data-id="element-548">
                          <Trash2 className="h-4 w-4" data-id="element-549" />
                        </button>
                      </div>)}
                  </div>
                </div>

                <div className="pt-2 border-t border-gray-700" data-id="element-550">
                  {deleteConfirm === idx ? <div className="flex items-center gap-3" data-id="element-551">
                      <p className="text-red-400 text-sm" data-id="element-552">
                        Удалить товар «{p.name.ru}»?
                      </p>
                      <button onClick={() => deleteProduct(idx)} className="text-xs bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-sm transition-colors" data-id="element-553">
                        Да, удалить
                      </button>
                      <button onClick={() => setDeleteConfirm(null)} className="text-xs text-gray-400 hover:text-white px-3 py-1.5 rounded-sm transition-colors" data-id="element-554">
                        Отмена
                      </button>
                    </div> : <button onClick={() => setDeleteConfirm(idx)} className="flex items-center gap-2 text-gray-500 hover:text-red-400 text-sm transition-colors" data-id="element-555">
                      <Trash2 className="h-4 w-4" data-id="element-556" /> Удалить товар
                    </button>}
                </div>
              </div>}
          </div>)}
      </div>
      <div className="flex items-center gap-3" data-id="element-557">
        <SaveBtn onClick={save} label="Сохранить каталог" data-id="element-558" />
        <button onClick={addProduct} className="flex items-center gap-2 border border-gray-600 hover:border-gray-400 text-gray-400 hover:text-white text-sm px-4 py-3 rounded-sm transition-colors" data-id="element-559">
          <Plus className="h-4 w-4" data-id="element-560" /> Добавить товар
        </button>
      </div>
    </div>;
}
// ── Stats ─────────────────────────────────────────────────────────
function StatsSection({
  onSave,
  adminLang
}: {
  onSave: () => void;
  adminLang: Language;
}) {
  const [stats, setStats] = useState<StatItem[]>(getContent().stats);
  const update = (idx: number, field: keyof StatItem, val: string) => {
    setStats(prev => prev.map((s, i) => i === idx ? {
      ...s,
      [field]: {
        ...(s[field] as any),
        [adminLang]: val
      }
    } : s));
  };
  const save = () => {
    const c = getContent();
    saveContent({
      ...c,
      stats
    });
    onSave();
  };
  return <div className="space-y-6" data-id="element-561">
      <div data-id="element-562">
        <h2 className="text-xl font-heading font-bold text-white mb-1" data-id="element-563">
          Статистика
        </h2>
        <p className="text-gray-400 text-sm" data-id="element-564">
          Цифры в тёмном блоке на главной странице.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-565">
        {stats.map((s, idx) => <div key={idx} className="bg-gray-900 border border-gray-700 rounded-sm p-4 space-y-3" data-id="element-566">
            <Field label="Значение (напр. 11+ или 100%)" value={s.value[adminLang] || ''} onChange={v => update(idx, 'value', v)} data-id="element-567" />

            <Field label="Подпись" value={s.label[adminLang] || ''} onChange={v => update(idx, 'label', v)} data-id="element-568" />
          </div>)}
      </div>
      <SaveBtn onClick={save} label="Сохранить статистику" data-id="element-569" />
    </div>;
}
// ── Map ───────────────────────────────────────────────────────────
function MapSection({
  onSave,
  adminLang
}: {
  onSave: () => void;
  adminLang: Language;
}) {
  const [map, setMap] = useState(getContent().map);
  const save = () => {
    const c = getContent();
    saveContent({
      ...c,
      map
    });
    onSave();
  };
  const previewSrc = `https://yandex.ru/map-widget/v1/?ll=${map.lng}%2C${map.lat}&z=${map.zoom}&pt=${map.lng}%2C${map.lat}%2Cpm2rdm`;
  return <div className="space-y-6" data-id="element-570">
      <div data-id="element-571">
        <h2 className="text-xl font-heading font-bold text-white mb-1" data-id="element-572">
          Карта на странице Контакты
        </h2>
        <p className="text-gray-400 text-sm" data-id="element-573">
          Координаты точки на карте Яндекс.
        </p>
      </div>

      <div className="bg-gray-900 border border-gray-700 rounded-sm p-4 text-sm text-gray-400" data-id="element-574">
        <p className="mb-2 font-medium text-gray-300" data-id="element-575">Как найти координаты:</p>
        <ol className="list-decimal list-inside space-y-1" data-id="element-576">
          <li data-id="element-577">
            Откройте{' '}
            <a href="https://yandex.ru/maps" target="_blank" rel="noopener noreferrer" className="text-red-400 underline" data-id="element-578">
              yandex.ru/maps
            </a>
          </li>
          <li data-id="element-579">Найдите нужный адрес</li>
          <li data-id="element-580">Нажмите правой кнопкой → «Что здесь?»</li>
          <li data-id="element-581">
            Скопируйте координаты (первое число — широта, второе — долгота)
          </li>
        </ol>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-582">
        <Field label="Широта (lat)" value={map.lat} onChange={v => setMap({
        ...map,
        lat: v
      })} placeholder="42.8746" data-id="element-583" />

        <Field label="Долгота (lng)" value={map.lng} onChange={v => setMap({
        ...map,
        lng: v
      })} placeholder="74.5698" data-id="element-584" />

        <Field label="Масштаб (zoom, 1-18)" value={String(map.zoom)} onChange={v => setMap({
        ...map,
        zoom: Number(v) || 16
      })} type="number" data-id="element-585" />

        <Field label="Подпись под картой" value={map.addressLabel[adminLang] || ''} onChange={v => setMap({
        ...map,
        addressLabel: {
          ...map.addressLabel,
          [adminLang]: v
        }
      })} placeholder="г. Бишкек, ул. ..." data-id="element-586" />
      </div>

      <div className="rounded-sm overflow-hidden h-48 bg-gray-800" data-id="element-587">
        <iframe src={previewSrc} width="100%" height="100%" frameBorder="0" title="Map Preview" className="grayscale" data-id="element-588" />
      </div>

      <SaveBtn onClick={save} label="Сохранить карту" data-id="element-589" />
    </div>;
}
// ── Team ──────────────────────────────────────────────────────────
function TeamSection({
  onSave,
  adminLang
}: {
  onSave: () => void;
  adminLang: Language;
}) {
  const [members, setMembers] = useState<TeamMember[]>(getContent().teamMembers);
  const [open, setOpen] = useState<number | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const update = (idx: number, field: keyof TeamMember, val: string) => {
    setMembers(prev => prev.map((m, i) => {
      if (i !== idx) return m;
      if (field === 'image' || field === 'id') {
        return {
          ...m,
          [field]: val
        };
      }
      return {
        ...m,
        [field]: {
          ...(m[field] as any),
          [adminLang]: val
        }
      };
    }));
  };
  const addMember = () => {
    const newMember: TeamMember = {
      id: `team_${Date.now()}`,
      name: {
        ru: 'Новый сотрудник',
        ky: 'Жаңы кызматкер'
      },
      role: {
        ru: 'Должность',
        ky: 'Кызмат'
      },
      image: ''
    };
    setMembers(prev => [...prev, newMember]);
    setOpen(members.length);
  };
  const deleteMember = (idx: number) => {
    setMembers(prev => prev.filter((_, i) => i !== idx));
    setDeleteConfirm(null);
    setOpen(null);
  };
  const save = () => {
    const c = getContent();
    saveContent({
      ...c,
      teamMembers: members
    });
    onSave();
  };
  return <div className="space-y-6" data-id="element-590">
      <div className="flex items-start justify-between" data-id="element-591">
        <div data-id="element-592">
          <h2 className="text-xl font-heading font-bold text-white mb-1" data-id="element-593">
            Наша команда
          </h2>
          <p className="text-gray-400 text-sm" data-id="element-594">
            Карточки сотрудников в разделе «О компании» на главной.
          </p>
        </div>
        <button onClick={addMember} className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 border border-gray-600 text-white text-sm px-4 py-2 rounded-sm transition-colors" data-id="element-595">
          <Plus className="h-4 w-4" data-id="element-596" /> Добавить
        </button>
      </div>

      <div className="space-y-3" data-id="element-597">
        {members.map((m, idx) => <div key={m.id} className="bg-gray-900 border border-gray-700 rounded-sm overflow-hidden" data-id="element-598">
            <button onClick={() => setOpen(open === idx ? null : idx)} className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-800 transition-colors" data-id="element-599">
              <div className="flex items-center gap-3" data-id="element-600">
                {m.image ? <img src={m.image} alt={m.name[adminLang] || m.name.ru} className="w-10 h-10 object-cover object-center rounded-full bg-gray-700 flex-shrink-0" onError={e => {
              ;
              (e.target as HTMLImageElement).style.display = 'none';
            }} data-id="element-601" /> : <div className="w-10 h-10 bg-gray-700 rounded-full flex-shrink-0 flex items-center justify-center" data-id="element-602">
                    <UsersIcon className="h-4 w-4 text-gray-500" data-id="element-603" />
                  </div>}
                <div data-id="element-604">
                  <p className="text-white font-medium text-sm" data-id="element-605">
                    {m.name[adminLang] || m.name.ru}
                  </p>
                  <p className="text-gray-500 text-xs" data-id="element-606">
                    {m.role[adminLang] || m.role.ru}
                  </p>
                </div>
              </div>
              {open === idx ? <ChevronUp className="h-4 w-4 text-gray-400" data-id="element-607" /> : <ChevronDown className="h-4 w-4 text-gray-400" data-id="element-608" />}
            </button>
            {open === idx && <div className="px-5 pb-5 space-y-4 border-t border-gray-700 pt-4" data-id="element-609">
                <Field label="Имя" value={m.name[adminLang] || ''} onChange={v => update(idx, 'name', v)} placeholder="Иван Иванов" data-id="element-610" />

                <Field label="Должность" value={m.role[adminLang] || ''} onChange={v => update(idx, 'role', v)} placeholder="Директор, инженер..." data-id="element-611" />

                <Field label="URL фото" value={m.image} onChange={v => update(idx, 'image', v)} placeholder="https://images.unsplash.com/..." data-id="element-612" />

                {m.image && <div className="flex justify-center" data-id="element-613">
                    <img src={m.image} alt="preview" className="h-32 w-32 object-cover object-center rounded-full bg-gray-700" data-id="element-614" />
                  </div>}
                <div className="pt-2 border-t border-gray-700" data-id="element-615">
                  {deleteConfirm === idx ? <div className="flex items-center gap-3" data-id="element-616">
                      <p className="text-red-400 text-sm" data-id="element-617">
                        Удалить «{m.name.ru}»?
                      </p>
                      <button onClick={() => deleteMember(idx)} className="text-xs bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-sm transition-colors" data-id="element-618">
                        Да, удалить
                      </button>
                      <button onClick={() => setDeleteConfirm(null)} className="text-xs text-gray-400 hover:text-white px-3 py-1.5 rounded-sm transition-colors" data-id="element-619">
                        Отмена
                      </button>
                    </div> : <button onClick={() => setDeleteConfirm(idx)} className="flex items-center gap-2 text-gray-500 hover:text-red-400 text-sm transition-colors" data-id="element-620">
                      <Trash2 className="h-4 w-4" data-id="element-621" /> Удалить сотрудника
                    </button>}
                </div>
              </div>}
          </div>)}
      </div>

      <div className="flex items-center gap-3" data-id="element-622">
        <SaveBtn onClick={save} label="Сохранить команду" data-id="element-623" />
        <button onClick={addMember} className="flex items-center gap-2 border border-gray-600 hover:border-gray-400 text-gray-400 hover:text-white text-sm px-4 py-3 rounded-sm transition-colors" data-id="element-624">
          <Plus className="h-4 w-4" data-id="element-625" /> Добавить сотрудника
        </button>
      </div>
    </div>;
}
// ── Contacts ──────────────────────────────────────────────────────
function ContactsSection({
  onSave
}: {
  onSave: () => void;
}) {
  const [cfg, setCfg] = useState(getSiteConfig().contacts);
  const save = () => {
    saveSiteConfig({
      ...getSiteConfig(),
      contacts: cfg
    });
    onSave();
  };
  return <div className="space-y-6" data-id="element-626">
      <div data-id="element-627">
        <h2 className="text-xl font-heading font-bold text-white mb-1" data-id="element-628">
          Контактная информация
        </h2>
        <p className="text-gray-400 text-sm" data-id="element-629">
          Отображается в футере и на странице контактов.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-630">
        <Field label="Телефон" value={cfg.phone} onChange={v => setCfg({
        ...cfg,
        phone: v
      })} placeholder="+996 502 100 636" data-id="element-631" />

        <Field label="Адрес" value={cfg.address} onChange={v => setCfg({
        ...cfg,
        address: v
      })} placeholder="г. Бишкек, ул. ..." data-id="element-632" />

        <Field label="Email" value={cfg.email} onChange={v => setCfg({
        ...cfg,
        email: v
      })} placeholder="info@yavuzburak.kg" data-id="element-633" />

        <Field label="Instagram (отображаемый)" value={cfg.instagram} onChange={v => setCfg({
        ...cfg,
        instagram: v
      })} placeholder="@yavuzburak.kg" data-id="element-634" />

        <Field label="Часы работы (будни)" value={cfg.workdaysHours} onChange={v => setCfg({
        ...cfg,
        workdaysHours: v
      })} placeholder="Пн-Пт: 9:00 - 18:00" data-id="element-635" />

        <Field label="Часы работы (выходные)" value={cfg.weekendHours} onChange={v => setCfg({
        ...cfg,
        weekendHours: v
      })} placeholder="Сб-Вс: Выходной" data-id="element-636" />
      </div>
      <SaveBtn onClick={save} label="Сохранить контакты" data-id="element-637" />
    </div>;
}
// ── Social ────────────────────────────────────────────────────────
function SocialSection({
  onSave
}: {
  onSave: () => void;
}) {
  const [cfg, setCfg] = useState(getSiteConfig().social);
  const save = () => {
    saveSiteConfig({
      ...getSiteConfig(),
      social: cfg
    });
    onSave();
  };
  const fields = [{
    key: 'instagram' as const,
    label: 'Instagram',
    icon: <Instagram className="h-4 w-4" data-id="element-638" />,
    placeholder: 'https://instagram.com/...'
  }, {
    key: 'whatsapp' as const,
    label: 'WhatsApp',
    icon: <MessageCircle className="h-4 w-4" data-id="element-639" />,
    placeholder: 'https://wa.me/996...'
  }, {
    key: 'telegram' as const,
    label: 'Telegram',
    icon: <Send className="h-4 w-4" data-id="element-640" />,
    placeholder: 'https://t.me/...'
  }, {
    key: 'facebook' as const,
    label: 'Facebook',
    icon: <Facebook className="h-4 w-4" data-id="element-641" />,
    placeholder: 'https://facebook.com/...'
  }, {
    key: 'youtube' as const,
    label: 'YouTube',
    icon: <Youtube className="h-4 w-4" data-id="element-642" />,
    placeholder: 'https://youtube.com/...'
  }];
  return <div className="space-y-6" data-id="element-643">
      <div data-id="element-644">
        <h2 className="text-xl font-heading font-bold text-white mb-1" data-id="element-645">
          Социальные сети
        </h2>
        <p className="text-gray-400 text-sm" data-id="element-646">
          Ссылки в футере. Оставьте пустым, чтобы скрыть иконку.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-647">
        {fields.map(f => <div key={f.key} className="space-y-1.5" data-id="element-648">
            <label className="text-sm font-medium text-gray-300 flex items-center gap-2" data-id="element-649">
              <span className="text-red-500" data-id="element-650">{f.icon}</span>
              {f.label}
            </label>
            <div className="flex gap-2" data-id="element-651">
              <input type="url" value={cfg[f.key]} onChange={e => setCfg({
            ...cfg,
            [f.key]: e.target.value
          })} placeholder={f.placeholder} className="flex-1 px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-sm text-white placeholder-gray-500 focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none text-sm" data-id="element-652" />

              {cfg[f.key] && <a href={cfg[f.key]} target="_blank" rel="noopener noreferrer" className="px-3 py-2.5 bg-gray-700 hover:bg-gray-600 rounded-sm text-gray-400 hover:text-white" data-id="element-653">
                  <ExternalLink className="h-4 w-4" data-id="element-654" />
                </a>}
            </div>
          </div>)}
      </div>
      <SaveBtn onClick={save} label="Сохранить ссылки" data-id="element-655" />
    </div>;
}
// ── Email ─────────────────────────────────────────────────────────
function EmailSection({
  onSave
}: {
  onSave: () => void;
}) {
  const [cfg, setCfg] = useState<EmailConfig>(getEmailConfig());
  const [showKey, setShowKey] = useState(false);
  const save = () => {
    saveEmailConfig(cfg);
    onSave();
  };
  const isConfigured = cfg.serviceId && cfg.templateId && cfg.publicKey;
  return <div className="space-y-6" data-id="element-656">
      <div data-id="element-657">
        <h2 className="text-xl font-heading font-bold text-white mb-1" data-id="element-658">
          Настройки форм и Email
        </h2>
        <p className="text-gray-400 text-sm" data-id="element-659">
          Для отправки писем используется EmailJS (бесплатно до 200 писем/мес).
        </p>
      </div>
      {!isConfigured && <div className="bg-yellow-900/30 border border-yellow-700/50 rounded-sm p-4 flex gap-3" data-id="element-660">
          <AlertTriangle className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" data-id="element-661" />
          <div className="text-sm" data-id="element-662">
            <p className="text-yellow-400 font-medium mb-2" data-id="element-663">
              EmailJS не настроен
            </p>
            <ol className="text-yellow-200/70 space-y-1 list-decimal list-inside" data-id="element-664">
              <li data-id="element-665">
                Зарегистрируйтесь на{' '}
                <a href="https://emailjs.com" target="_blank" rel="noopener noreferrer" className="text-yellow-400 underline" data-id="element-666">
                  emailjs.com
                </a>
              </li>
              <li data-id="element-667">Добавьте Email Service (подключите почту 3.13.13@mail.ru)</li>
              <li data-id="element-668">
                Создайте Email Template с переменными:{' '}
                <code className="bg-black/30 px-1 rounded text-xs" data-id="element-669">
                  {'{{from_name}}'}
                </code>
                ,{' '}
                <code className="bg-black/30 px-1 rounded text-xs" data-id="element-670">
                  {'{{phone}}'}
                </code>
                ,{' '}
                <code className="bg-black/30 px-1 rounded text-xs" data-id="element-671">
                  {'{{message}}'}
                </code>
              </li>
              <li data-id="element-672">Скопируйте Service ID, Template ID и Public Key ниже</li>
            </ol>
          </div>
        </div>}
      {isConfigured && <div className="bg-green-900/30 border border-green-700/50 rounded-sm p-4 flex items-center gap-3" data-id="element-673">
          <CheckCircle className="h-5 w-5 text-green-500" data-id="element-674" />
          <p className="text-green-400 text-sm font-medium" data-id="element-675">
            EmailJS настроен. Письма отправляются на {cfg.toEmail}
          </p>
        </div>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-676">
        <Field label="Email получателя" value={cfg.toEmail} onChange={v => setCfg({
        ...cfg,
        toEmail: v
      })} placeholder="3.13.13@mail.ru" hint="На этот адрес будут приходить все заявки" data-id="element-677" />

        <Field label="EmailJS Service ID" value={cfg.serviceId} onChange={v => setCfg({
        ...cfg,
        serviceId: v
      })} placeholder="service_xxxxxxx" data-id="element-678" />

        <Field label="EmailJS Template ID" value={cfg.templateId} onChange={v => setCfg({
        ...cfg,
        templateId: v
      })} placeholder="template_xxxxxxx" data-id="element-679" />

        <div className="space-y-1.5" data-id="element-680">
          <label className="text-sm font-medium text-gray-300" data-id="element-681">
            EmailJS Public Key
          </label>
          <div className="relative" data-id="element-682">
            <input type={showKey ? 'text' : 'password'} value={cfg.publicKey} onChange={e => setCfg({
            ...cfg,
            publicKey: e.target.value
          })} placeholder="xxxxxxxxxxxxxxxxxxxx" className="w-full px-4 py-2.5 pr-10 bg-gray-800 border border-gray-700 rounded-sm text-white placeholder-gray-500 focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none text-sm" data-id="element-683" />

            <button type="button" onClick={() => setShowKey(!showKey)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300" data-id="element-684">
              {showKey ? <EyeOff className="h-4 w-4" data-id="element-685" /> : <Eye className="h-4 w-4" data-id="element-686" />}
            </button>
          </div>
        </div>
      </div>
      <SaveBtn onClick={save} label="Сохранить настройки" data-id="element-687" />
    </div>;
}
// ── SEO Section ───────────────────────────────────────────────────
function SEOSection({
  onSave
}: {
  onSave: () => void;
}) {
  const [seo, setSeo] = useState<SEOStore>(getSEO());
  const [activePage, setActivePage] = useState<keyof SEOStore>('home');
  const pages: {
    key: keyof SEOStore;
    label: string;
  }[] = [{
    key: 'home',
    label: 'Главная'
  }, {
    key: 'catalog',
    label: 'Каталог'
  }, {
    key: 'contact',
    label: 'Контакты'
  }, {
    key: 'partner',
    label: 'Партнеры'
  }];
  const updatePage = (field: keyof PageSEO, val: string) => {
    setSeo(prev => ({
      ...prev,
      [activePage]: {
        ...prev[activePage],
        [field]: val
      }
    }));
  };
  const save = () => {
    saveSEO(seo);
    onSave();
  };
  const current = seo[activePage];
  return <div className="space-y-6" data-id="element-688">
      <div data-id="element-689">
        <h2 className="text-xl font-heading font-bold text-white mb-1" data-id="element-690">
          SEO / Мета-теги
        </h2>
        <p className="text-gray-400 text-sm" data-id="element-691">
          Заголовки и описания страниц для поисковых систем.
        </p>
      </div>

      <div className="bg-gray-900 border border-gray-700 rounded-sm p-3 text-xs text-gray-400" data-id="element-692">
        <p className="font-medium text-gray-300 mb-1" data-id="element-693">Рекомендации:</p>
        <ul className="space-y-0.5 list-disc list-inside" data-id="element-694">
          <li data-id="element-695">Title: 50–60 символов</li>
          <li data-id="element-696">Description: 150–160 символов</li>
          <li data-id="element-697">Keywords: через запятую, 5–10 слов</li>
        </ul>
      </div>

      {/* Page tabs */}
      <div className="flex gap-1 bg-gray-900 p-1 rounded-sm border border-gray-700" data-id="element-698">
        {pages.map(p => <button key={p.key} onClick={() => setActivePage(p.key)} className={`flex-1 py-2 text-xs font-medium rounded-sm transition-colors ${activePage === p.key ? 'bg-red-600 text-white' : 'text-gray-400 hover:text-white'}`} data-id="element-699">
            {p.label}
          </button>)}
      </div>

      <div className="space-y-4" data-id="element-700">
        <div className="space-y-1.5" data-id="element-701">
          <div className="flex items-center justify-between" data-id="element-702">
            <label className="text-sm font-medium text-gray-300" data-id="element-703">
              Title (заголовок страницы)
            </label>
            <span className={`text-xs ${current.title.length > 60 ? 'text-red-400' : 'text-gray-500'}`} data-id="element-704">
              {current.title.length}/60
            </span>
          </div>
          <input value={current.title} onChange={e => updatePage('title', e.target.value)} className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-sm text-white placeholder-gray-500 focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none text-sm" data-id="element-705" />

          {/* Preview */}
          <div className="bg-gray-900 border border-gray-700 rounded-sm p-3 mt-2" data-id="element-706">
            <p className="text-blue-400 text-sm font-medium truncate" data-id="element-707">
              {current.title || 'Заголовок страницы'}
            </p>
            <p className="text-green-600 text-xs mt-0.5" data-id="element-708">yavuzburak.kg</p>
            <p className="text-gray-400 text-xs mt-1 line-clamp-2" data-id="element-709">
              {current.description || 'Описание страницы...'}
            </p>
          </div>
        </div>

        <div className="space-y-1.5" data-id="element-710">
          <div className="flex items-center justify-between" data-id="element-711">
            <label className="text-sm font-medium text-gray-300" data-id="element-712">
              Description (описание)
            </label>
            <span className={`text-xs ${current.description.length > 160 ? 'text-red-400' : 'text-gray-500'}`} data-id="element-713">
              {current.description.length}/160
            </span>
          </div>
          <textarea value={current.description} onChange={e => updatePage('description', e.target.value)} rows={3} className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-sm text-white placeholder-gray-500 focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none text-sm resize-none" data-id="element-714" />
        </div>

        <div className="space-y-1.5" data-id="element-715">
          <label className="text-sm font-medium text-gray-300" data-id="element-716">
            Keywords (ключевые слова)
          </label>
          <input value={current.keywords} onChange={e => updatePage('keywords', e.target.value)} placeholder="краска, Бишкек, фасад..." className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-sm text-white placeholder-gray-500 focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none text-sm" data-id="element-717" />

          <p className="text-xs text-gray-500" data-id="element-718">Через запятую</p>
        </div>
      </div>

      <SaveBtn onClick={save} label="Сохранить SEO" data-id="element-719" />
    </div>;
}
// ── Password ──────────────────────────────────────────────────────
function PasswordSection({
  onSave
}: {
  onSave: () => void;
}) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const save = () => {
    setError('');
    if (current !== getAdminPassword()) {
      setError('Неверный текущий пароль');
      return;
    }
    if (next.length < 6) {
      setError('Минимум 6 символов');
      return;
    }
    if (next !== confirm) {
      setError('Пароли не совпадают');
      return;
    }
    setAdminPassword(next);
    setCurrent('');
    setNext('');
    setConfirm('');
    onSave();
  };
  return <div className="space-y-6" data-id="element-720">
      <div data-id="element-721">
        <h2 className="text-xl font-heading font-bold text-white mb-1" data-id="element-722">
          Безопасность
        </h2>
        <p className="text-gray-400 text-sm" data-id="element-723">
          Смена пароля для входа в админ-панель.
        </p>
      </div>
      <div className="max-w-sm space-y-4" data-id="element-724">
        <Field label="Текущий пароль" value={current} onChange={setCurrent} type="password" placeholder="••••••••" data-id="element-725" />

        <Field label="Новый пароль" value={next} onChange={setNext} type="password" placeholder="Минимум 6 символов" data-id="element-726" />

        <Field label="Повторите новый пароль" value={confirm} onChange={setConfirm} type="password" placeholder="••••••••" data-id="element-727" />

        {error && <p className="text-red-400 text-sm flex items-center gap-1" data-id="element-728">
            <AlertTriangle className="h-3.5 w-3.5" data-id="element-729" />
            {error}
          </p>}
        <SaveBtn onClick={save} label="Изменить пароль" data-id="element-730" />
      </div>
    </div>;
}
// ── Login ─────────────────────────────────────────────────────────
const MAX_ATTEMPTS = 5;
const LOCKOUT_SECONDS = 3600;
function getLoginLockout(): {
  attempts: number;
  lockedUntil: number;
} {
  try {
    const stored = localStorage.getItem('yb_admin_lockout');
    if (stored) return JSON.parse(stored);
  } catch {}
  return {
    attempts: 0,
    lockedUntil: 0
  };
}
function setLoginLockout(attempts: number, lockedUntil: number) {
  localStorage.setItem('yb_admin_lockout', JSON.stringify({
    attempts,
    lockedUntil
  }));
}
function LoginScreen({
  onLogin
}: {
  onLogin: () => void;
}) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [attempts, setAttempts] = useState(() => getLoginLockout().attempts);
  const [lockedUntil, setLockedUntil] = useState(() => getLoginLockout().lockedUntil);
  const [countdown, setCountdown] = useState(0);
  useEffect(() => {
    const tick = () => {
      const now = Date.now();
      if (lockedUntil > now) {
        setCountdown(Math.ceil((lockedUntil - now) / 1000));
      } else {
        setCountdown(0);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [lockedUntil]);
  const isLocked = countdown > 0;
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isLocked) return;
    if (password === getAdminPassword()) {
      setLoginLockout(0, 0);
      onLogin();
    } else {
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      setError(true);
      setPassword('');
      if (newAttempts >= MAX_ATTEMPTS) {
        const until = Date.now() + LOCKOUT_SECONDS * 1000;
        setLockedUntil(until);
        setLoginLockout(newAttempts, until);
      } else {
        setLoginLockout(newAttempts, 0);
      }
    }
  };
  return <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4" data-id="element-731">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} className="w-full max-w-sm" data-id="element-732">
        <div className="text-center mb-8" data-id="element-733">
          <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/hyseuuq2warchstz2tvf3s/f7c74e3b-3f4e-439c-8223-13536fb8e81c/YB_A1.svg" alt="Logo" className="h-16 w-auto mx-auto mb-4" data-id="element-734" />

          <h1 className="font-heading text-2xl font-bold text-white uppercase tracking-wider" data-id="element-735">
            Админ-панель
          </h1>
          <p className="text-gray-500 text-sm mt-1" data-id="element-736">YAVUZ BURAK</p>
        </div>
        <form onSubmit={submit} className="bg-gray-900 border border-gray-800 rounded-sm p-8 space-y-5" data-id="element-737">
          <div className="space-y-1.5" data-id="element-738">
            <label className="text-sm font-medium text-gray-300" data-id="element-739">Пароль</label>
            <div className="relative" data-id="element-740">
              <input type={showPw ? 'text' : 'password'} value={password} onChange={e => {
              setPassword(e.target.value);
              setError(false);
            }} className={`w-full px-4 py-3 pr-10 bg-gray-800 border rounded-sm text-white placeholder-gray-500 focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none transition-all ${error ? 'border-red-500' : 'border-gray-700'}`} placeholder="Введите пароль" autoFocus data-id="element-741" />

              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300" data-id="element-742">
                {showPw ? <EyeOff className="h-4 w-4" data-id="element-743" /> : <Eye className="h-4 w-4" data-id="element-744" />}
              </button>
            </div>
            {error && !isLocked && <p className="text-red-400 text-xs" data-id="element-745">
                Неверный пароль
                {attempts > 0 && attempts < MAX_ATTEMPTS && <span className="text-gray-500 ml-1" data-id="element-746">
                    (попытка {attempts} из {MAX_ATTEMPTS})
                  </span>}
              </p>}
          </div>

          {isLocked && <div className="flex items-center gap-2 bg-red-900/30 border border-red-800 rounded-sm px-4 py-3" data-id="element-747">
              <AlertTriangle className="h-4 w-4 text-red-400 flex-shrink-0" data-id="element-748" />
              <p className="text-red-400 text-xs" data-id="element-749">
                Слишком много попыток. Вход временно заблокирован.
              </p>
            </div>}

          <button type="submit" disabled={isLocked} className={`w-full font-medium uppercase tracking-wide py-3 rounded-sm transition-colors ${isLocked ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700 text-white'}`} data-id="element-750">
            {isLocked ? 'Вход заблокирован' : 'Войти'}
          </button>
        </form>
      </motion.div>
    </div>;
}
// ── Application Areas Section ─────────────────────────────────────
function AreasSection({
  onSave,
  adminLang
}: {
  onSave: () => void;
  adminLang: Language;
}) {
  const [areas, setAreas] = useState<AreaBlock[]>(getContent().applicationAreas);
  const [open, setOpen] = useState<number | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const update = (idx: number, field: keyof AreaBlock, val: string) => {
    setAreas(prev => prev.map((a, i) => {
      if (i !== idx) return a;
      if (field === 'icon' || field === 'id') {
        return {
          ...a,
          [field]: val
        };
      }
      return {
        ...a,
        [field]: {
          ...(a[field] as any),
          [adminLang]: val
        }
      };
    }));
  };
  const addArea = () => {
    const newArea: AreaBlock = {
      id: `area_${Date.now()}`,
      icon: 'Package',
      title: {
        ru: 'Новый блок',
        ky: 'Жаңы блок'
      },
      description: {
        ru: 'Описание блока.',
        ky: 'Блоктун сүрөттөлүшү.'
      }
    };
    setAreas(prev => [...prev, newArea]);
    setOpen(areas.length);
  };
  const deleteArea = (idx: number) => {
    setAreas(prev => prev.filter((_, i) => i !== idx));
    setDeleteConfirm(null);
    setOpen(null);
  };
  const save = () => {
    const c = getContent();
    saveContent({
      ...c,
      applicationAreas: areas
    });
    onSave();
  };
  return <div className="space-y-6" data-id="element-751">
      <div className="flex items-start justify-between" data-id="element-752">
        <div data-id="element-753">
          <h2 className="text-xl font-heading font-bold text-white mb-1" data-id="element-754">
            Главная — Сферы применения
          </h2>
          <p className="text-gray-400 text-sm" data-id="element-755">
            Карточки с иконками на главной странице.
          </p>
        </div>
        <button onClick={addArea} className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 border border-gray-600 text-white text-sm px-4 py-2 rounded-sm transition-colors" data-id="element-756">
          <Plus className="h-4 w-4" data-id="element-757" /> Добавить блок
        </button>
      </div>
      <div className="space-y-3" data-id="element-758">
        {areas.map((a, idx) => <div key={a.id} className="bg-gray-900 border border-gray-700 rounded-sm overflow-hidden" data-id="element-759">
            <button onClick={() => setOpen(open === idx ? null : idx)} className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-800 transition-colors" data-id="element-760">
              <div className="flex items-center gap-3" data-id="element-761">
                <span className="text-gray-400" data-id="element-762">
                  {renderIcon(a.icon, 'h-5 w-5')}
                </span>
                <p className="text-white font-medium text-sm" data-id="element-763">
                  {a.title[adminLang] || a.title.ru}
                </p>
              </div>
              {open === idx ? <ChevronUp className="h-4 w-4 text-gray-400" data-id="element-764" /> : <ChevronDown className="h-4 w-4 text-gray-400" data-id="element-765" />}
            </button>
            {open === idx && <div className="px-5 pb-5 space-y-4 border-t border-gray-700 pt-4" data-id="element-766">
                <IconPicker value={a.icon} onChange={v => update(idx, 'icon', v)} data-id="element-767" />

                <Field label="Заголовок" value={a.title[adminLang] || ''} onChange={v => update(idx, 'title', v)} data-id="element-768" />

                <TextArea label="Описание" value={a.description[adminLang] || ''} onChange={v => update(idx, 'description', v)} rows={3} data-id="element-769" />

                <div className="pt-2 border-t border-gray-700" data-id="element-770">
                  {deleteConfirm === idx ? <div className="flex items-center gap-3" data-id="element-771">
                      <p className="text-red-400 text-sm" data-id="element-772">
                        Удалить «{a.title.ru}»?
                      </p>
                      <button onClick={() => deleteArea(idx)} className="text-xs bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-sm" data-id="element-773">
                        Да
                      </button>
                      <button onClick={() => setDeleteConfirm(null)} className="text-xs text-gray-400 hover:text-white px-3 py-1.5 rounded-sm" data-id="element-774">
                        Отмена
                      </button>
                    </div> : <button onClick={() => setDeleteConfirm(idx)} className="flex items-center gap-2 text-gray-500 hover:text-red-400 text-sm transition-colors" data-id="element-775">
                      <Trash2 className="h-4 w-4" data-id="element-776" /> Удалить блок
                    </button>}
                </div>
              </div>}
          </div>)}
      </div>
      <div className="flex gap-3" data-id="element-777">
        <SaveBtn onClick={save} label="Сохранить блоки" data-id="element-778" />
        <button onClick={addArea} className="flex items-center gap-2 border border-gray-600 hover:border-gray-400 text-gray-400 hover:text-white text-sm px-4 py-3 rounded-sm transition-colors" data-id="element-779">
          <Plus className="h-4 w-4" data-id="element-780" /> Добавить блок
        </button>
      </div>
    </div>;
}
// ── Partner Page Section ──────────────────────────────────────────
function PartnerAdminSection({
  onSave,
  adminLang
}: {
  onSave: () => void;
  adminLang: Language;
}) {
  const content = getContent();
  const [pg, setPg] = useState(content.partnerPage);
  const [ps, setPs] = useState(content.partnerSection);
  const [benefits, setBenefits] = useState<PartnerBenefit[]>(content.partnerBenefits);
  const [steps, setSteps] = useState<PartnerStep[]>(content.partnerSteps);
  const [tab, setTab] = useState<'text' | 'benefits' | 'steps' | 'section'>('text');
  const [openB, setOpenB] = useState<number | null>(null);
  const [openS, setOpenS] = useState<number | null>(null);
  const [delB, setDelB] = useState<number | null>(null);
  const [delS, setDelS] = useState<number | null>(null);
  const updatePg = (field: keyof typeof pg, val: string) => {
    setPg(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        [adminLang]: val
      }
    }));
  };
  const updatePs = (field: keyof typeof ps, val: string) => {
    setPs(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        [adminLang]: val
      }
    }));
  };
  const updateBenefit = (idx: number, field: keyof PartnerBenefit, val: string) => {
    setBenefits(prev => prev.map((b, i) => {
      if (i !== idx) return b;
      if (field === 'icon' || field === 'id') {
        return {
          ...b,
          [field]: val
        };
      }
      return {
        ...b,
        [field]: {
          ...(b[field] as any),
          [adminLang]: val
        }
      };
    }));
  };
  const addBenefit = () => {
    setBenefits(prev => [...prev, {
      id: `b_${Date.now()}`,
      icon: 'Star',
      title: {
        ru: 'Новое преимущество',
        ky: 'Жаңы артыкчылык'
      },
      description: {
        ru: 'Описание.',
        ky: 'Сүрөттөлүшү.'
      }
    }]);
    setOpenB(benefits.length);
  };
  const deleteBenefit = (idx: number) => {
    setBenefits(prev => prev.filter((_, i) => i !== idx));
    setDelB(null);
    setOpenB(null);
  };
  const updateStep = (idx: number, field: keyof PartnerStep, val: string) => {
    setSteps(prev => prev.map((s, i) => {
      if (i !== idx) return s;
      if (field === 'num' || field === 'id') {
        return {
          ...s,
          [field]: val
        };
      }
      return {
        ...s,
        [field]: {
          ...(s[field] as any),
          [adminLang]: val
        }
      };
    }));
  };
  const addStep = () => {
    const num = String(steps.length + 1).padStart(2, '0');
    setSteps(prev => [...prev, {
      id: `s_${Date.now()}`,
      num,
      title: {
        ru: 'Новый шаг',
        ky: 'Жаңы кадам'
      },
      desc: {
        ru: 'Описание шага.',
        ky: 'Кадамдын сүрөттөлүшү.'
      }
    }]);
    setOpenS(steps.length);
  };
  const deleteStep = (idx: number) => {
    setSteps(prev => prev.filter((_, i) => i !== idx));
    setDelS(null);
    setOpenS(null);
  };
  const save = () => {
    const c = getContent();
    saveContent({
      ...c,
      partnerPage: pg,
      partnerSection: ps,
      partnerBenefits: benefits,
      partnerSteps: steps
    });
    onSave();
  };
  const tabs = [{
    id: 'text' as const,
    label: 'Тексты страницы'
  }, {
    id: 'benefits' as const,
    label: 'Преимущества'
  }, {
    id: 'steps' as const,
    label: 'Шаги'
  }, {
    id: 'section' as const,
    label: 'Блок на главной'
  }];
  return <div className="space-y-6" data-id="element-781">
      <div data-id="element-782">
        <h2 className="text-xl font-heading font-bold text-white mb-1" data-id="element-783">
          Страница партнеров
        </h2>
        <p className="text-gray-400 text-sm" data-id="element-784">
          Управление всем контентом страницы BM PARTNER.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-900 p-1 rounded-sm border border-gray-700 flex-wrap" data-id="element-785">
        {tabs.map(t => <button key={t.id} onClick={() => setTab(t.id)} className={`flex-1 min-w-[120px] py-2 text-xs font-medium rounded-sm transition-colors ${tab === t.id ? 'bg-red-600 text-white' : 'text-gray-400 hover:text-white'}`} data-id="element-786">
            {t.label}
          </button>)}
      </div>

      {/* Texts */}
      {tab === 'text' && <div className="space-y-5" data-id="element-787">
          <p className="text-gray-500 text-xs uppercase tracking-wider font-medium" data-id="element-788">
            Hero секция
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-789">
            <Field label="Лейбл (маленький текст)" value={pg.heroLabel[adminLang] || ''} onChange={v => updatePg('heroLabel', v)} data-id="element-790" />

            <Field label="Заголовок (до акцента)" value={pg.heroTitle[adminLang] || ''} onChange={v => updatePg('heroTitle', v)} data-id="element-791" />

            <Field label="Акцентная часть заголовка" value={pg.heroTitleAccent[adminLang] || ''} onChange={v => updatePg('heroTitleAccent', v)} data-id="element-792" />
          </div>
          <TextArea label="Описание" value={pg.heroDescription[adminLang] || ''} onChange={v => updatePg('heroDescription', v)} data-id="element-793" />

          <p className="text-gray-500 text-xs uppercase tracking-wider font-medium pt-2" data-id="element-794">
            Секция преимуществ
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-795">
            <Field label="Лейбл" value={pg.benefitsLabel[adminLang] || ''} onChange={v => updatePg('benefitsLabel', v)} data-id="element-796" />

            <Field label="Заголовок" value={pg.benefitsTitle[adminLang] || ''} onChange={v => updatePg('benefitsTitle', v)} data-id="element-797" />

            <Field label="Акцент" value={pg.benefitsTitleAccent[adminLang] || ''} onChange={v => updatePg('benefitsTitleAccent', v)} data-id="element-798" />
          </div>

          <p className="text-gray-500 text-xs uppercase tracking-wider font-medium pt-2" data-id="element-799">
            Секция шагов
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-800">
            <Field label="Лейбл" value={pg.stepsLabel[adminLang] || ''} onChange={v => updatePg('stepsLabel', v)} data-id="element-801" />

            <Field label="Заголовок" value={pg.stepsTitle[adminLang] || ''} onChange={v => updatePg('stepsTitle', v)} data-id="element-802" />

            <Field label="Акцент" value={pg.stepsTitleAccent[adminLang] || ''} onChange={v => updatePg('stepsTitleAccent', v)} data-id="element-803" />
          </div>

          <p className="text-gray-500 text-xs uppercase tracking-wider font-medium pt-2" data-id="element-804">
            Форма заявки
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-805">
            <Field label="Лейбл" value={pg.formLabel[adminLang] || ''} onChange={v => updatePg('formLabel', v)} data-id="element-806" />

            <Field label="Заголовок" value={pg.formTitle[adminLang] || ''} onChange={v => updatePg('formTitle', v)} data-id="element-807" />

            <Field label="Акцент" value={pg.formTitleAccent[adminLang] || ''} onChange={v => updatePg('formTitleAccent', v)} data-id="element-808" />
          </div>
          <TextArea label="Описание формы" value={pg.formDescription[adminLang] || ''} onChange={v => updatePg('formDescription', v)} rows={2} data-id="element-809" />
        </div>}

      {/* Benefits */}
      {tab === 'benefits' && <div className="space-y-4" data-id="element-810">
          <div className="flex justify-end" data-id="element-811">
            <button onClick={addBenefit} className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 border border-gray-600 text-white text-sm px-4 py-2 rounded-sm transition-colors" data-id="element-812">
              <Plus className="h-4 w-4" data-id="element-813" /> Добавить
            </button>
          </div>
          {benefits.map((b, idx) => <div key={b.id} className="bg-gray-900 border border-gray-700 rounded-sm overflow-hidden" data-id="element-814">
              <button onClick={() => setOpenB(openB === idx ? null : idx)} className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-800 transition-colors" data-id="element-815">
                <div className="flex items-center gap-3" data-id="element-816">
                  <span className="text-gray-400" data-id="element-817">
                    {renderIcon(b.icon, 'h-5 w-5')}
                  </span>
                  <p className="text-white font-medium text-sm" data-id="element-818">
                    {b.title[adminLang] || b.title.ru}
                  </p>
                </div>
                {openB === idx ? <ChevronUp className="h-4 w-4 text-gray-400" data-id="element-819" /> : <ChevronDown className="h-4 w-4 text-gray-400" data-id="element-820" />}
              </button>
              {openB === idx && <div className="px-5 pb-5 space-y-4 border-t border-gray-700 pt-4" data-id="element-821">
                  <IconPicker value={b.icon} onChange={v => updateBenefit(idx, 'icon', v)} data-id="element-822" />

                  <Field label="Заголовок" value={b.title[adminLang] || ''} onChange={v => updateBenefit(idx, 'title', v)} data-id="element-823" />

                  <TextArea label="Описание" value={b.description[adminLang] || ''} onChange={v => updateBenefit(idx, 'description', v)} rows={3} data-id="element-824" />

                  <div className="pt-2 border-t border-gray-700" data-id="element-825">
                    {delB === idx ? <div className="flex items-center gap-3" data-id="element-826">
                        <p className="text-red-400 text-sm" data-id="element-827">
                          Удалить «{b.title.ru}»?
                        </p>
                        <button onClick={() => deleteBenefit(idx)} className="text-xs bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-sm" data-id="element-828">
                          Да
                        </button>
                        <button onClick={() => setDelB(null)} className="text-xs text-gray-400 hover:text-white px-3 py-1.5 rounded-sm" data-id="element-829">
                          Отмена
                        </button>
                      </div> : <button onClick={() => setDelB(idx)} className="flex items-center gap-2 text-gray-500 hover:text-red-400 text-sm transition-colors" data-id="element-830">
                        <Trash2 className="h-4 w-4" data-id="element-831" /> Удалить
                      </button>}
                  </div>
                </div>}
            </div>)}
        </div>}

      {/* Steps */}
      {tab === 'steps' && <div className="space-y-4" data-id="element-832">
          <div className="flex justify-end" data-id="element-833">
            <button onClick={addStep} className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 border border-gray-600 text-white text-sm px-4 py-2 rounded-sm transition-colors" data-id="element-834">
              <Plus className="h-4 w-4" data-id="element-835" /> Добавить шаг
            </button>
          </div>
          {steps.map((s, idx) => <div key={s.id} className="bg-gray-900 border border-gray-700 rounded-sm overflow-hidden" data-id="element-836">
              <button onClick={() => setOpenS(openS === idx ? null : idx)} className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-800 transition-colors" data-id="element-837">
                <div className="flex items-center gap-3" data-id="element-838">
                  <span className="text-red-500 font-bold text-lg font-heading" data-id="element-839">
                    {s.num}
                  </span>
                  <p className="text-white font-medium text-sm" data-id="element-840">
                    {s.title[adminLang] || s.title.ru}
                  </p>
                </div>
                {openS === idx ? <ChevronUp className="h-4 w-4 text-gray-400" data-id="element-841" /> : <ChevronDown className="h-4 w-4 text-gray-400" data-id="element-842" />}
              </button>
              {openS === idx && <div className="px-5 pb-5 space-y-4 border-t border-gray-700 pt-4" data-id="element-843">
                  <div className="grid grid-cols-2 gap-4" data-id="element-844">
                    <Field label="Номер (напр. 01)" value={s.num} onChange={v => updateStep(idx, 'num', v)} data-id="element-845" />

                    <Field label="Заголовок" value={s.title[adminLang] || ''} onChange={v => updateStep(idx, 'title', v)} data-id="element-846" />
                  </div>
                  <TextArea label="Описание" value={s.desc[adminLang] || ''} onChange={v => updateStep(idx, 'desc', v)} rows={2} data-id="element-847" />

                  <div className="pt-2 border-t border-gray-700" data-id="element-848">
                    {delS === idx ? <div className="flex items-center gap-3" data-id="element-849">
                        <p className="text-red-400 text-sm" data-id="element-850">
                          Удалить шаг «{s.title.ru}»?
                        </p>
                        <button onClick={() => deleteStep(idx)} className="text-xs bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-sm" data-id="element-851">
                          Да
                        </button>
                        <button onClick={() => setDelS(null)} className="text-xs text-gray-400 hover:text-white px-3 py-1.5 rounded-sm" data-id="element-852">
                          Отмена
                        </button>
                      </div> : <button onClick={() => setDelS(idx)} className="flex items-center gap-2 text-gray-500 hover:text-red-400 text-sm transition-colors" data-id="element-853">
                        <Trash2 className="h-4 w-4" data-id="element-854" /> Удалить
                      </button>}
                  </div>
                </div>}
            </div>)}
        </div>}

      {/* Partner section on homepage */}
      {tab === 'section' && <div className="space-y-4" data-id="element-855">
          <p className="text-gray-400 text-sm" data-id="element-856">
            Блок «BM PARTNER» на главной странице. Показывает первые 4
            преимущества из списка выше.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-857">
            <Field label="Лейбл" value={ps.label[adminLang] || ''} onChange={v => updatePs('label', v)} data-id="element-858" />

            <Field label="Заголовок (до акцента)" value={ps.title[adminLang] || ''} onChange={v => updatePs('title', v)} data-id="element-859" />

            <Field label="Акцентная часть" value={ps.titleAccent[adminLang] || ''} onChange={v => updatePs('titleAccent', v)} data-id="element-860" />

            <Field label="Текст кнопки" value={ps.ctaText[adminLang] || ''} onChange={v => updatePs('ctaText', v)} data-id="element-861" />
          </div>
          <TextArea label="Описание" value={ps.description[adminLang] || ''} onChange={v => updatePs('description', v)} rows={3} data-id="element-862" />
        </div>}

      <SaveBtn onClick={save} label="Сохранить" data-id="element-863" />
    </div>;
}
// ── Hero Section ──────────────────────────────────────────────────
function HeroAdminSection({
  onSave,
  adminLang
}: {
  onSave: () => void;
  adminLang: Language;
}) {
  const [hero, setHero] = useState(getContent().hero);
  const update = (field: keyof typeof hero, val: string) => {
    setHero(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        [adminLang]: val
      }
    }));
  };
  const save = () => {
    const c = getContent();
    saveContent({
      ...c,
      hero
    });
    onSave();
  };
  return <div className="space-y-6" data-id="element-864">
      <div data-id="element-865">
        <h2 className="text-xl font-heading font-bold text-white mb-1" data-id="element-866">
          Главная — Hero
        </h2>
        <p className="text-gray-400 text-sm" data-id="element-867">Первый экран главной страницы.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-868">
        <Field label="Подзаголовок (маленький текст)" value={hero.subtitle[adminLang] || ''} onChange={v => update('subtitle', v)} data-id="element-869" />

        <Field label="Заголовок" value={hero.title2[adminLang] || ''} onChange={v => update('title2', v)} data-id="element-870" />

        <Field label="Кнопка: Каталог" value={hero.btnCatalog[adminLang] || ''} onChange={v => update('btnCatalog', v)} data-id="element-871" />

        <Field label="Кнопка: Контакты" value={hero.btnContact[adminLang] || ''} onChange={v => update('btnContact', v)} data-id="element-872" />

        <Field label="Подсказка (для цвета)" value={hero.hint[adminLang] || ''} onChange={v => update('hint', v)} data-id="element-873" />
      </div>

      <TextArea label="Описание" value={hero.description[adminLang] || ''} onChange={v => update('description', v)} rows={3} data-id="element-874" />

      <SaveBtn onClick={save} label="Сохранить" data-id="element-875" />
    </div>;
}
// ── About Section ─────────────────────────────────────────────────
function AboutAdminSection({
  onSave,
  adminLang
}: {
  onSave: () => void;
  adminLang: Language;
}) {
  const [about, setAbout] = useState(getContent().about);
  const [openF, setOpenF] = useState<number | null>(null);
  const update = (field: keyof typeof about, val: string) => {
    if (field === 'image') {
      setAbout(prev => ({
        ...prev,
        [field]: val
      }));
      return;
    }
    setAbout(prev => ({
      ...prev,
      [field]: {
        ...(prev[field] as any),
        [adminLang]: val
      }
    }));
  };
  const updateFeature = (idx: number, field: string, val: string) => {
    setAbout(prev => ({
      ...prev,
      features: prev.features.map((f, i) => {
        if (i !== idx) return f;
        if (field === 'icon') return {
          ...f,
          icon: val
        };
        return {
          ...f,
          [field]: {
            ...(f as any)[field],
            [adminLang]: val
          }
        };
      })
    }));
  };
  const save = () => {
    const c = getContent();
    saveContent({
      ...c,
      about
    });
    onSave();
  };
  return <div className="space-y-6" data-id="element-876">
      <div data-id="element-877">
        <h2 className="text-xl font-heading font-bold text-white mb-1" data-id="element-878">
          Главная — О компании
        </h2>
        <p className="text-gray-400 text-sm" data-id="element-879">
          Блок с описанием и преимуществами.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-880">
        <Field label="Лейбл" value={about.label[adminLang] || ''} onChange={v => update('label', v)} data-id="element-881" />

        <Field label="Заголовок 1" value={about.title1[adminLang] || ''} onChange={v => update('title1', v)} data-id="element-882" />

        <Field label="Заголовок 2 (акцент)" value={about.title2[adminLang] || ''} onChange={v => update('title2', v)} data-id="element-883" />

        <Field label="URL картинки" value={about.image} onChange={v => update('image', v)} data-id="element-884" />
      </div>

      <TextArea label="Описание" value={about.description[adminLang] || ''} onChange={v => update('description', v)} rows={3} data-id="element-885" />

      <div className="pt-4 border-t border-gray-800" data-id="element-886">
        <h3 className="text-lg font-bold text-white mb-4" data-id="element-887">
          Преимущества (4 блока)
        </h3>
        <div className="space-y-3" data-id="element-888">
          {about.features.map((f, idx) => <div key={f.id} className="bg-gray-900 border border-gray-700 rounded-sm overflow-hidden" data-id="element-889">
              <button onClick={() => setOpenF(openF === idx ? null : idx)} className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-800" data-id="element-890">
                <div className="flex items-center gap-3" data-id="element-891">
                  <span className="text-gray-400" data-id="element-892">
                    {renderIcon(f.icon, 'h-5 w-5')}
                  </span>
                  <p className="text-white font-medium text-sm" data-id="element-893">
                    {f.title[adminLang] || f.title.ru}
                  </p>
                </div>
                {openF === idx ? <ChevronUp className="h-4 w-4 text-gray-400" data-id="element-894" /> : <ChevronDown className="h-4 w-4 text-gray-400" data-id="element-895" />}
              </button>
              {openF === idx && <div className="px-5 pb-5 space-y-4 border-t border-gray-700 pt-4" data-id="element-896">
                  <IconPicker value={f.icon} onChange={v => updateFeature(idx, 'icon', v)} data-id="element-897" />

                  <Field label="Заголовок" value={f.title[adminLang] || ''} onChange={v => updateFeature(idx, 'title', v)} data-id="element-898" />

                  <TextArea label="Текст" value={f.text[adminLang] || ''} onChange={v => updateFeature(idx, 'text', v)} rows={2} data-id="element-899" />
                </div>}
            </div>)}
        </div>
      </div>

      <div className="pt-4 border-t border-gray-800" data-id="element-900">
        <h3 className="text-lg font-bold text-white mb-4" data-id="element-901">Заголовок команды</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-902">
          <Field label="Заголовок 1" value={about.teamTitle1[adminLang] || ''} onChange={v => update('teamTitle1', v)} data-id="element-903" />

          <Field label="Заголовок 2 (акцент)" value={about.teamTitle2[adminLang] || ''} onChange={v => update('teamTitle2', v)} data-id="element-904" />
        </div>
        <div className="mt-4" data-id="element-905">
          <Field label="Описание" value={about.teamDesc[adminLang] || ''} onChange={v => update('teamDesc', v)} data-id="element-906" />
        </div>
      </div>

      <SaveBtn onClick={save} label="Сохранить" data-id="element-907" />
    </div>;
}
// ── Clients Section ───────────────────────────────────────────────
function ClientsAdminSection({
  onSave
}: {
  onSave: () => void;
}) {
  const [clients, setClients] = useState(getContent().clients);
  const [open, setOpen] = useState<number | null>(null);
  const [delConfirm, setDelConfirm] = useState<number | null>(null);
  const update = (idx: number, field: keyof ClientItem, val: string) => {
    setClients(prev => prev.map((c, i) => i === idx ? {
      ...c,
      [field]: val
    } : c));
  };
  const addClient = () => {
    setClients(prev => [...prev, {
      id: `c_${Date.now()}`,
      name: 'Новый клиент',
      logoUrl: ''
    }]);
    setOpen(clients.length);
  };
  const deleteClient = (idx: number) => {
    setClients(prev => prev.filter((_, i) => i !== idx));
    setDelConfirm(null);
    setOpen(null);
  };
  const save = () => {
    const c = getContent();
    saveContent({
      ...c,
      clients
    });
    onSave();
  };
  return <div className="space-y-6" data-id="element-908">
      <div className="flex items-start justify-between" data-id="element-909">
        <div data-id="element-910">
          <h2 className="text-xl font-heading font-bold text-white mb-1" data-id="element-911">
            Клиенты
          </h2>
          <p className="text-gray-400 text-sm" data-id="element-912">
            Бегущая строка с логотипами/названиями.
          </p>
        </div>
        <button onClick={addClient} className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 border border-gray-600 text-white text-sm px-4 py-2 rounded-sm" data-id="element-913">
          <Plus className="h-4 w-4" data-id="element-914" /> Добавить
        </button>
      </div>

      <div className="space-y-3" data-id="element-915">
        {clients.map((c, idx) => <div key={c.id} className="bg-gray-900 border border-gray-700 rounded-sm overflow-hidden" data-id="element-916">
            <button onClick={() => setOpen(open === idx ? null : idx)} className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-800" data-id="element-917">
              <div className="flex items-center gap-3" data-id="element-918">
                {c.logoUrl ? <img src={c.logoUrl} alt={c.name} className="h-6 w-auto object-contain bg-white p-1 rounded" onError={e => (e.target as HTMLImageElement).style.display = 'none'} data-id="element-919" /> : <div className="h-6 w-6 bg-gray-700 rounded flex items-center justify-center text-xs font-bold" data-id="element-920">
                    {c.name.substring(0, 2).toUpperCase()}
                  </div>}
                <p className="text-white font-medium text-sm" data-id="element-921">{c.name}</p>
              </div>
              {open === idx ? <ChevronUp className="h-4 w-4 text-gray-400" data-id="element-922" /> : <ChevronDown className="h-4 w-4 text-gray-400" data-id="element-923" />}
            </button>
            {open === idx && <div className="px-5 pb-5 space-y-4 border-t border-gray-700 pt-4" data-id="element-924">
                <Field label="Название компании" value={c.name} onChange={v => update(idx, 'name', v)} data-id="element-925" />

                <Field label="URL логотипа (необязательно)" value={c.logoUrl} onChange={v => update(idx, 'logoUrl', v)} placeholder="https://..." data-id="element-926" />

                <div className="pt-2 border-t border-gray-700" data-id="element-927">
                  {delConfirm === idx ? <div className="flex items-center gap-3" data-id="element-928">
                      <p className="text-red-400 text-sm" data-id="element-929">
                        Удалить «{c.name}»?
                      </p>
                      <button onClick={() => deleteClient(idx)} className="text-xs bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-sm" data-id="element-930">
                        Да
                      </button>
                      <button onClick={() => setDelConfirm(null)} className="text-xs text-gray-400 hover:text-white px-3 py-1.5 rounded-sm" data-id="element-931">
                        Отмена
                      </button>
                    </div> : <button onClick={() => setDelConfirm(idx)} className="flex items-center gap-2 text-gray-500 hover:text-red-400 text-sm" data-id="element-932">
                      <Trash2 className="h-4 w-4" data-id="element-933" /> Удалить
                    </button>}
                </div>
              </div>}
          </div>)}
      </div>
      <SaveBtn onClick={save} label="Сохранить" data-id="element-934" />
    </div>;
}
// ── Main ──────────────────────────────────────────────────────────
export function AdminPage() {
  const [authed, setAuthed] = useState(() => sessionStorage.getItem('yb_admin_auth') === '1');
  const [section, setSection] = useState<Section>('preview');
  const [saved, setSaved] = useState(false);
  const [adminLang, setAdminLang] = useState<Language>('ru');
  const login = () => {
    sessionStorage.setItem('yb_admin_auth', '1');
    setAuthed(true);
  };
  const logout = () => {
    sessionStorage.removeItem('yb_admin_auth');
    setAuthed(false);
  };
  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };
  if (!authed) return <LoginScreen onLogin={login} data-id="element-935" />;
  const groups = ['Контент', 'Настройки'];
  return <div className="min-h-screen bg-gray-950 text-white flex" data-id="element-936">
      <SaveBanner show={saved} data-id="element-937" />

      {/* Sidebar */}
      <aside className="w-64 bg-gray-900 border-r border-gray-800 flex flex-col flex-shrink-0" data-id="element-938">
        <div className="p-5 border-b border-gray-800" data-id="element-939">
          <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/hyseuuq2warchstz2tvf3s/f7c74e3b-3f4e-439c-8223-13536fb8e81c/YB_A1.svg" alt="Logo" className="h-10 w-auto mb-3" data-id="element-940" />

          <p className="text-xs text-gray-500 uppercase tracking-wider font-medium" data-id="element-941">
            Панель управления
          </p>
        </div>

        <nav className="flex-1 p-3 overflow-y-auto" data-id="element-942">
          {groups.map(group => <div key={group} className="mb-4" data-id="element-943">
              <p className="text-xs text-gray-600 uppercase tracking-wider font-medium px-3 mb-2" data-id="element-944">
                {group}
              </p>
              {NAV.filter(n => n.group === group).map(item => <button key={item.id} onClick={() => setSection(item.id)} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-all mb-0.5 ${section === item.id ? 'bg-red-600 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800'}`} data-id="element-945">
                  {item.icon}
                  <span className="truncate" data-id="element-946">{item.label}</span>
                  {section === item.id && <ChevronRight className="h-3.5 w-3.5 ml-auto flex-shrink-0" data-id="element-947" />}
                </button>)}
            </div>)}
        </nav>

        <div className="p-3 border-t border-gray-800" data-id="element-948">
          <a href="/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-gray-500 hover:text-white text-sm px-3 py-2 rounded-sm hover:bg-gray-800 transition-colors mb-1" data-id="element-949">
            <ExternalLink className="h-4 w-4" data-id="element-950" /> Открыть сайт
          </a>
          <button onClick={logout} className="w-full flex items-center gap-2 text-gray-500 hover:text-red-400 text-sm px-3 py-2 rounded-sm hover:bg-gray-800 transition-colors" data-id="element-951">
            <LogOut className="h-4 w-4" data-id="element-952" /> Выйти
          </button>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 overflow-auto" data-id="element-953">
        <div className="max-w-3xl mx-auto p-8" data-id="element-954">
          {/* Language Toggle */}
          {['hero', 'about', 'preview', 'areas', 'team', 'catalog', 'partner', 'stats', 'map'].includes(section) && <div className="flex gap-2 mb-8 bg-gray-900 p-1.5 rounded-sm border border-gray-800 w-fit" data-id="element-955">
              <button onClick={() => setAdminLang('ru')} className={`px-4 py-1.5 text-sm font-medium rounded-sm transition-colors ${adminLang === 'ru' ? 'bg-red-600 text-white' : 'text-gray-400 hover:text-white'}`} data-id="element-956">
                🇷🇺 Русский
              </button>
              <button onClick={() => setAdminLang('ky')} className={`px-4 py-1.5 text-sm font-medium rounded-sm transition-colors ${adminLang === 'ky' ? 'bg-red-600 text-white' : 'text-gray-400 hover:text-white'}`} data-id="element-957">
                🇰🇬 Кыргызча
              </button>
            </div>}

          <AnimatePresence mode="wait" data-id="element-958">
            <motion.div key={section} initial={{
            opacity: 0,
            x: 10
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -10
          }} transition={{
            duration: 0.2
          }} data-id="element-959">
              {section === 'hero' && <HeroAdminSection onSave={handleSave} adminLang={adminLang} data-id="element-960" />}
              {section === 'about' && <AboutAdminSection onSave={handleSave} adminLang={adminLang} data-id="element-961" />}
              {section === 'clients' && <ClientsAdminSection onSave={handleSave} data-id="element-962" />}
              {section === 'preview' && <PreviewSection onSave={handleSave} adminLang={adminLang} data-id="element-963" />}
              {section === 'areas' && <AreasSection onSave={handleSave} adminLang={adminLang} data-id="element-964" />}
              {section === 'team' && <TeamSection onSave={handleSave} adminLang={adminLang} data-id="element-965" />}
              {section === 'catalog' && <CatalogSection onSave={handleSave} adminLang={adminLang} data-id="element-966" />}
              {section === 'partner' && <PartnerAdminSection onSave={handleSave} adminLang={adminLang} data-id="element-967" />}
              {section === 'stats' && <StatsSection onSave={handleSave} adminLang={adminLang} data-id="element-968" />}
              {section === 'map' && <MapSection onSave={handleSave} adminLang={adminLang} data-id="element-969" />}
              {section === 'seo' && <SEOSection onSave={handleSave} data-id="element-970" />}
              {section === 'contacts' && <ContactsSection onSave={handleSave} data-id="element-971" />}
              {section === 'social' && <SocialSection onSave={handleSave} data-id="element-972" />}
              {section === 'email' && <EmailSection onSave={handleSave} data-id="element-973" />}
              {section === 'password' && <PasswordSection onSave={handleSave} data-id="element-974" />}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>
    </div>;
}